import { dateFormat, numberFormat, round, safeParseDate, safeParseInt } from "shared/util"
import { IconButton } from "@mui/material"
import { OpenInNew as LinkIcon } from '@mui/icons-material'
import { GridColDef, GridRowId } from "@mui/x-data-grid"

import { GridColumnDefMap, GridFilter } from "./types"

export const valueFormatter = ({
	value,
	unit,
	addCanma,
	showZero,
	fixed
}: {
	value: number | string
	unit?: string
	addCanma?: boolean
	showZero?: boolean
	fixed?: number
}): string => {
	if (typeof value === 'number') {
		if (value === Infinity) return ''
		if (showZero === false && value === 0) return ''
		if (fixed !== undefined) {
			return valueFormatter({
				value: round(value, fixed),
				unit, addCanma, showZero
			})
		}
		if (unit) {
			if (unit === '%') {
				return value ? `${(value * 100).toFixed(2)} ${unit}` : ''
			}
			if (addCanma === false) return value ? `${value} ${unit}` : ''
			return value ? `${numberFormat(value)} ${unit}` : ''
		} else {
			if (addCanma === false) return value ? `${value}` : ''
			return value ? `${numberFormat(value)}` : ''
		}
	} else if (value) {
		return unit ? `${value} ${unit}` : value
	}
	return ''
}
export const formatter = {
	rank: (value: number | string) => valueFormatter({ value, unit: '位' }),
	age: (value: number | string) => valueFormatter({ value, unit: '歳', addCanma: false }),
	year: (value: number | string) => valueFormatter({ value, unit: '年', addCanma: false }),
	bedCount: (value: number | string) => valueFormatter({ value, unit: '床' }),
	personCount: (value: number | string) => valueFormatter({ value, unit: '人' }),
	facilityCount: (value: number | string) => valueFormatter({ value, unit: '施設' }),
	count: (value: number | string) => valueFormatter({ value, unit: '件', showZero: false }),
	score: (value: number | string) => valueFormatter({ value, unit: 'pt', showZero: false }),
	second: (value: number | string) => valueFormatter({ value, unit: '秒' }),
	percent: (value: number | string) => valueFormatter({ value, unit: '%' }),
	currency: (value: number | string) => valueFormatter({ value, unit: '円', fixed: 0 }),
}

type Def = Omit<GridColDef, "field" | "headerName">

export const columnBase = {
	rowNo: {
		align: 'right',
		width: 80,
		type: 'number'
	} as Def,
	rank: {
		width: 80,
		align: 'right',
		type: 'number',
		valueFormatter: formatter.rank,
		valueGetter: (value) => value === '' ? Infinity : value,
		suffix: '位',
		filterType: 'range'
	} as Def,
	age: {
		align: 'right',
		width: 70,
		type: 'number',
		valueFormatter: formatter.age,
		suffix: '歳',
		filterType: 'histgram',
		histgram: { unit: 10 },
	} as Def,
	bedCount: {
		align: 'right',
		width: 80,
		type: 'number',
		valueFormatter: formatter.bedCount,
		suffix: '床',
		filterType: 'histgram',
		histgram: { unit: 100 }
	} as Def,
	personCount: {
		align: 'right',
		width: 80,
		type: 'number',
		suffix: '人',
		filterType: 'histgram',
		valueFormatter: formatter.personCount
	} as Def,
	facilityCount: {
		align: 'right',
		width: 80,
		type: 'number',
		valueFormatter: formatter.facilityCount
	} as Def,
	score: {
		align: 'right',
		width: 80,
		type: 'number',
		valueFormatter: formatter.score,
		suffix: 'pt',
		filterType: 'histgram',
		histgram: { unit: 10 }
	} as Def,
	count: {
		align: 'right',
		width: 80,
		type: 'number',
		valueFormatter: formatter.count,
		suffix: '件',
		filterType: 'histgram'
	} as Def,
	percent: {
		align: 'right',
		width: 80,
		type: 'number',
		valueFormatter: formatter.percent,
		suffix: '%',
		filterType: 'histgram',
		histgram: { unit: 10 }
	} as Def,
	date: {
		width: 220,
		valueFormatter: (value: number | string) => {
			if (typeof value === 'string') {
				return dateFormat(value, { wareki: true, nullText: value })
			}
			return ''
		},
		filterType: 'date'
	} as Def,
	action: {
		openById: (onOpen: (id: GridRowId) => void) => ({
			align: 'center',
			width: 50,
			sortable: false,
			downloadable: false,
			disableColumnMenu: true,
			renderCell: (params) => (
				<IconButton
					size="small"
					onClick={() => onOpen(params.id)}
					sx={{ margin: -1 }}
				>
					<LinkIcon fontSize="small"/>
				</IconButton>
			)
		} as Def),
		openByValue: (onOpen: (value: string) => void) => ({
			align: 'center',
			width: 50,
			sortable: false,
			downloadable: false,
			disableColumnMenu: true,
			renderCell: (params) => (
				<IconButton
					size="small"
					onClick={() => onOpen(params.value)}
					sx={{ margin: -1 }}
				>
					<LinkIcon fontSize="small"/>
				</IconButton>
			)
		} as Def)
	}
}

export const isFilterd = <T,>(
	record: T,
	filter: GridFilter,
	columnsDef: GridColumnDefMap<T>
) => {
	let def = columnsDef[filter.field]
	if (!def) return false
	if (def.filterField) {
		def = columnsDef[def.filterField]
		if (!def) return false
	}
	const value = def.value(record)
	const arr = Array.isArray(value) ? value : [value]
	if (filter.candidates) {
		const candidates = filter.candidates
		if (arr.length === 0) arr.push('(空)')
		return !arr.some(r => {
			const v = (typeof r === 'string') ? r : (typeof r === 'number') ? `${r}` : null
			return candidates.includes(v || '(空)')
		})
	} else if (filter.textPatterns) {
		const patterns = filter.textPatterns.map(pattern => pattern.split(' '))
		const texts = arr.flatMap(value => (typeof value === 'string') ? value : (typeof value === 'number') ? `${value}` : [])
		return !texts.some(text => patterns.some(pattern => pattern.every(word => text.indexOf(word) >= 0)))
	} else if (filter.range) {
		const { from, fromEq, to, toEq } = filter.range
		const values = arr.map(value => (typeof value === 'string') ? safeParseInt(value) : (typeof value === 'number') ? value : null)
		return !values.some(value => {
			const num = value || 0
			if (from) {
				if (fromEq === false) {
					if (from >= num) return false
				} else {
					if (from > num) return false
				}
			}
			if (to) {
				if (toEq === false) {
					if (to <= num) return false
				} else {
					if (to < num) return false
				}
			}
			return true
		})
	} else if (filter.dateRange) {
		const { from, to } = filter.dateRange
		const times = arr.flatMap(value => (typeof value === 'string') ? safeParseDate(value)?.getTime() ?? [] : [])
		return !times.some(time => {
			if (from && (from > time)) return false
			if (to && (to + (24 * 60 * 60 * 1000) < time)) return false
			return true
		})
	} else if (filter.weekDays || filter.hours) {
		const { weekDays, hours } = filter
		const texts = arr.flatMap(value => (typeof value === 'string') ? value : [])
		return !texts.some(text => {
			if (weekDays && weekDays.length > 0) {
				if (weekDays.some(w => text.startsWith(w)) === false) return false
			}
			if (hours && hours.length > 0) {
				if (hours.some(h => text.endsWith(`${h}～${h + 2}時`)) === false) return false
			}
			return true
		})
	}
	return true
}
